import React, { useContext, useEffect, useState } from "react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ChatOutlined from "@material-ui/icons/ChatOutlined";
import DashboardOutlined from "@material-ui/icons/DashboardOutlined";
import EmailOutlined from "@material-ui/icons/EmailOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ImportantDevicesOutlined from "@material-ui/icons/ImportantDevicesOutlined";
import RateReviewOutlined from "@material-ui/icons/RateReviewOutlined";
import FavoriteBorder from "@material-ui/icons/FavoriteBorder";
import { getSubMenuFromMainMenu, isSubMenuActive } from "../common";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@material-ui/core";
import { iSideBarNavigation } from "../../types/props";
import { ChatAppStateContext, AppStateContext } from "../../App";
import { Link, useLocation } from "react-router-dom";
import { shopPerm } from "../../utils/shopPermissions";

const MAIN_MENU_PARENT_ICONS: Record<string, React.ReactElement> = {
  dashboard: (
    <DashboardOutlined
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
  email: (
    <EmailOutlined
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
  chats: (
    <ChatOutlined
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
  reviews: (
    <RateReviewOutlined
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
  popups: (
    <ImportantDevicesOutlined
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
  wishlists: (
    <FavoriteBorder
      fontSize="small"
      className="ew-mobile-nav-accordion__parent-icon-svg"
    />
  ),
};

function parentIconForMainLink(link: string): React.ReactElement {
  return MAIN_MENU_PARENT_ICONS[link] ?? MAIN_MENU_PARENT_ICONS.dashboard;
}

export default function NestedList({
  onRequestClose,
}: {
  onRequestClose?: () => void;
}) {
  const { t: tNav } = useTranslation("nav");
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) =>
    (
      _event: React.ChangeEvent<Record<string, unknown>>,
      isExpanded: boolean,
    ) => {
      setExpanded(isExpanded ? panel : false);
    };

  const { chat_stats } = useContext(ChatAppStateContext);
  const { shop, loggedInUser } = useContext(AppStateContext);
  const [menu_options, setMenuOptions] = useState<iSideBarNavigation[]>([]);

  useEffect(() => {
    const perms = loggedInUser?.shop_permissions;
    const menus = [
      {
        link: "/dashboard",
        text: tNav("main.home"),
        perm: "shop.dashboard.read",
        svg_node: undefined,
        "data-tut": "reactour__header_dashboard",
      },
      {
        link: "/email",
        text: tNav("main.email"),
        perm: "shop.email.read",
        svg_node: undefined,
        "data-tut": "reactour__header_email",
      },
      {
        link: "/reviews",
        text: tNav("main.reviews"),
        perm: "shop.reviews.read",
        svg_node: undefined,
        "data-tut": "reactour__header_reviews",
      },
      {
        link: "/chats",
        text: tNav("main.chats"),
        perm: "shop.chats_inbox.read",
        badgeCount: "",
        svg_node: undefined,
        "data-tut": "reactour__header_chats",
      },
      {
        link: "/popups",
        text: tNav("main.popups"),
        perm: "shop.popups.read",
        svg_node: undefined,
        "data-tut": "reactour__header_popups",
      },
      {
        link: "/wishlists",
        text: tNav("main.wishlists"),
        perm: "shop.wishlists.read",
        svg_node: undefined,
        "data-tut": "reactour__header_wishlists",
      },
    ];
    const filtered = perms
      ? menus.filter((m) => shopPerm(perms, m.perm)).map(({ perm, ...rest }) => rest)
      : menus.map(({ perm, ...rest }) => rest);
    setMenuOptions([...filtered]);
  }, [chat_stats, tNav, loggedInUser?.shop_permissions]);

  const location = useLocation();

  return (
    <div className="ew-mobile-nav-list">
      {menu_options.map((menu, i) => {
        const link = menu.link.replace("/", "");
        const panelId = `ew-mobile-nav-panel-${i}`;

        if (menu.disabled && menu.comingSoon) {
          return (
            <div key={menu.link + i} className="ew-mobile-nav-soon">
              <span className="ew-mobile-nav-soon__badge">
                {tNav("main.comingSoon")}
              </span>
              <div className="ew-mobile-nav-soon__row" aria-disabled="true">
                <span
                  className="ew-mobile-nav-accordion__parent-icon"
                  aria-hidden
                >
                  {parentIconForMainLink(link)}
                </span>
                <span className="ew-mobile-nav-accordion__title">
                  {menu.text}
                </span>
              </div>
            </div>
          );
        }

        return (
          <Accordion
            className="ew-mobile-nav-accordion"
            key={menu.link + i}
            expanded={expanded === panelId}
            onChange={handleChange(panelId)}
            elevation={0}
          >
            <AccordionSummary
              className="ew-mobile-nav-accordion__summary"
              expandIcon={
                <ExpandMoreIcon className="ew-mobile-nav-accordion__chevron" />
              }
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
            >
              <span
                className="ew-mobile-nav-accordion__parent-icon"
                aria-hidden
              >
                {parentIconForMainLink(link)}
              </span>
              <span className="ew-mobile-nav-accordion__title">
                {menu.text}
              </span>
            </AccordionSummary>
            <AccordionDetails className="ew-mobile-nav-accordion__details">
              <div className="ew-mobile-nav-sublist">
                <List
                  component="nav"
                  aria-labelledby={`${panelId}-header`}
                  className="ew-mobile-nav-sublinks"
                >
                  {getSubMenuFromMainMenu(
                    link,
                    tNav,
                    loggedInUser?.shop_permissions,
                  ).map((item, j) => {
                    const isActive = isSubMenuActive(
                      location.pathname,
                      item.link,
                    );

                    // Logic to hide "Add your own server" based on plan
                    let isHiddenServer = false;
                    if (item.link === "/email/your-server") {
                      if (shop?.customer?.plan?.options) {
                        try {
                          const options = JSON.parse(
                            shop.customer.plan.options,
                          );
                          if (
                            options &&
                            options.sending_server_option === "system"
                          ) {
                            isHiddenServer = true;
                          }
                        } catch (e) {
                          isHiddenServer = true;
                        }
                      } else {
                        isHiddenServer = true;
                      }
                    }

                    if (isHiddenServer) return null;

                    return (
                      <ListItem
                        button
                        component={Link}
                        to={item.link}
                        className={`ew-mobile-nav-sublink ${isActive ? "ew-mobile-nav-sublink--active" : ""}`}
                        key={item.link + j}
                        data-tut={item["data-tut"]}
                        onClick={() => onRequestClose?.()}
                      >
                        <ListItemIcon className="ew-mobile-nav-sublink__icon">
                          {isActive && item.active_node}
                          {(!isActive || !item.active_node) && item.svg_node}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <span className="ew-mobile-nav-sublink__text">
                              {item.text}
                            </span>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}
